import { Capacitor, SystemBars, SystemBarsStyle } from "@capacitor/core";
import { useTheme } from "next-themes";
import { useEffect, useRef } from "react";
import {
  type Appearance,
  VidimoAppearance,
} from "@/lib/native/vidimo-appearance";

const MEDIA = "(prefers-color-scheme: dark)";
const APPEARANCES: ReadonlySet<string> = new Set<Appearance>([
  "light",
  "dark",
  "system",
]);

function isAppearance(theme: string | undefined): theme is Appearance {
  return theme !== undefined && APPEARANCES.has(theme);
}

/**
 * next-themes applies the `light` / `dark` class from `prefers-color-scheme` when
 * the stored theme is `system`, but that can get out of sync (effect ordering,
 * WebView quirk, or resuming the app after the OS theme changed).
 * Re-sync the document to match `matchMedia` while `theme === "system"`.
 */
function reconcileSystemToMedia(themeIsSystem: boolean) {
  if (!themeIsSystem) {
    return;
  }
  const prefersDark = window.matchMedia(MEDIA).matches;
  const root = document.documentElement;
  const isDark = root.classList.contains("dark");
  if (prefersDark === isDark) {
    return;
  }
  root.classList.remove("light", "dark");
  root.classList.add(prefersDark ? "dark" : "light");
  root.style.colorScheme = prefersDark ? "dark" : "light";
}

/**
 * `theme-color` should follow the actual OS / browser appearance when the user
 * picked System; `resolvedTheme` from context can lag or disagree with
 * `prefers-color-scheme` in that mode.
 */
function useThemeColorMeta() {
  const { theme, resolvedTheme } = useTheme();

  useEffect(() => {
    const setMeta = (isDark: boolean) => {
      const meta = document.querySelector('meta[name="theme-color"]');
      if (meta) {
        meta.setAttribute("content", isDark ? "#0a0a0a" : "#ffffff");
      }
    };

    if (theme !== "system") {
      setMeta(resolvedTheme === "dark");
      return;
    }

    const mql = window.matchMedia(MEDIA);
    const update = () => setMeta(mql.matches);
    update();
    mql.addEventListener("change", update);
    return () => mql.removeEventListener("change", update);
  }, [theme, resolvedTheme]);
}

/**
 * After next-themes runs, ensure `html.light` / `html.dark` matches
 * `prefers-color-scheme` whenever the app theme is "system".
 */
function useReconcileSystemClass() {
  const { theme } = useTheme();
  const themeRef = useRef(theme);
  themeRef.current = theme;

  useEffect(() => {
    if (theme !== "system") {
      return;
    }

    const run = () => reconcileSystemToMedia(themeRef.current === "system");

    run();
    const t = window.setTimeout(run, 0);

    const mql = window.matchMedia(MEDIA);
    mql.addEventListener("change", run);
    const onVis = () => {
      if (document.visibilityState === "visible") {
        run();
      }
    };
    const onFocus = () => run();

    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onFocus);

    return () => {
      clearTimeout(t);
      mql.removeEventListener("change", run);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onFocus);
    };
  }, [theme]);
}

/**
 * SystemBars resolves its Default style once and re-applies the stored result
 * on a configuration change, so the bar icons are resolved again after every
 * night-mode move.
 */
async function restyleSystemBars() {
  try {
    await SystemBars.setStyle({ style: SystemBarsStyle.Default });
  } catch (err) {
    console.error("[appearance] system bars not restyled", err);
  }
}

/** Android's night mode follows the in-app theme, for the bar strips. */
function useNativeAppearance() {
  const { theme } = useTheme();

  useEffect(() => {
    if (Capacitor.getPlatform() !== "android" || !isAppearance(theme)) {
      return;
    }
    const apply = async () => {
      try {
        await VidimoAppearance.set({ appearance: theme });
      } catch (err) {
        console.error("[appearance] native night mode not set", err);
        return;
      }
      await restyleSystemBars();
    };
    apply();
    if (theme !== "system") {
      return;
    }
    const mql = window.matchMedia(MEDIA);
    mql.addEventListener("change", restyleSystemBars);
    return () => mql.removeEventListener("change", restyleSystemBars);
  }, [theme]);
}

/**
 * Subscribes in-app theme + OS appearance for correct colors and PWA bar tint.
 * Must render as a child of `ThemeProvider` (e.g. in `main.tsx`).
 */
export function SystemThemeSync() {
  useReconcileSystemClass();
  useThemeColorMeta();
  useNativeAppearance();
  return null;
}
